import React, { FC, useCallback, useId, useState } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvButton,
	EvCaptionDefault,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	EvTextInput,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_ADD_VALUE_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_ADD_VALUE_INPUT_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';

import { LABEL_PANEL_VALIDATION } from '../../LabelPanel.utils';

import { LABEL_VALUES_LABELS } from './LabelValuesSection.labels';
import { LabelValuesSectionProps } from './LabelValuesSection.types';
import { ValuesTable } from './values-table';

export const LabelValuesSection: FC<LabelValuesSectionProps> = ({
	values,
	onAddValue,
	onRemoveValue,
	onToggleDashboardVisibility
}) => {
	const [newValue, setNewValue] = useState('');
	const [addValueError, setAddValueError] = useState<string | undefined>();
	const inputLabelId = useId();

	const handleAddValue = useCallback(() => {
		setNewValue((currentValue) => {
			const error = onAddValue(currentValue);
			setAddValueError(error);
			return error ? currentValue : '';
		});
	}, [onAddValue]);

	const handleInputChange = useCallback((value: string) => {
		setNewValue(value);
		setAddValueError(undefined);
	}, []);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				handleAddValue();
			}
		},
		[handleAddValue]
	);

	return (
		<EvFlexColumn gap="LARGE">
			<EvFlexColumn gap="MEDIUM">
				<EvHeadingSmall as="h4" color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.ADD_SUBSECTION_TITLE}
				</EvHeadingSmall>
				<EvBodyMedium color={TEXT_COLOR.CONTENT_3}>
					{LABEL_VALUES_LABELS.ADD_SUBSECTION_SUBTITLE}
				</EvBodyMedium>
				<EvBodyMedium as="label" id={inputLabelId} color={TEXT_COLOR.CONTENT_2}>
					{LABEL_VALUES_LABELS.ADD_VALUE_LABEL}
				</EvBodyMedium>
				<EvFlexRow gap="MEDIUM" align="START">
					<EvFlexColumn flex="1">
						<EvTextInput
							ariaLabelledBy={inputLabelId}
							value={newValue}
							onChange={handleInputChange}
							placeholder={LABEL_VALUES_LABELS.ADD_VALUE_PLACEHOLDER}
							maxLength={String(LABEL_PANEL_VALIDATION.VALUE_MAX_LENGTH)}
							dataAutomationId={LABEL_MANAGEMENT_PANEL_ADD_VALUE_INPUT_DAID}
							onKeyDown={handleKeyDown}
						/>
					</EvFlexColumn>
					<EvButton
						type={BUTTON_TYPES.GHOST}
						title={LABEL_VALUES_LABELS.ADD_VALUE_BUTTON}
						onClick={handleAddValue}
						dataAutomationId={LABEL_MANAGEMENT_PANEL_ADD_VALUE_BUTTON_DAID}
					>
						{LABEL_VALUES_LABELS.ADD_VALUE_BUTTON}
					</EvButton>
				</EvFlexRow>
				{addValueError && (
					<EvCaptionDefault color={TEXT_COLOR.RED}>{addValueError}</EvCaptionDefault>
				)}
			</EvFlexColumn>

			{values.length > 0 && (
				<EvFlexColumn gap="MEDIUM">
					<EvHeadingSmall as="h4" color={TEXT_COLOR.CONTENT_2}>
						{LABEL_VALUES_LABELS.EXISTING_SUBSECTION_TITLE}
					</EvHeadingSmall>
					<ValuesTable
						values={values}
						onRemoveValue={onRemoveValue}
						onToggleDashboardVisibility={onToggleDashboardVisibility}
					/>
				</EvFlexColumn>
			)}
		</EvFlexColumn>
	);
};
